import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowLeft, Bell, Mail, MessageSquare, Smartphone } from 'lucide-react';
import { Switch } from '@/components/ui';
import { LoadingSpinner } from '../shared';
import { UserNotificationPreferencesService } from '../../api/services/UserNotificationPreferencesService';
import { Routemize_Constants_NotificationType } from '../../api/models/Routemize_Constants_NotificationType';
import type { Routemize_DTOs_UserNotificationPreferenceDTO } from '../../api/models/Routemize_DTOs_UserNotificationPreferenceDTO';
import type { Routemize_DTOs_UpdateUserNotificationPreferenceDTO } from '../../api/models/Routemize_DTOs_UpdateUserNotificationPreferenceDTO';
import { useVoiceMessageNotificationsVisible } from '../../hooks/useVoiceMessageNotificationsVisible';

interface UserNotificationPreferencesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface NotificationTypeMeta {
  type: Routemize_Constants_NotificationType;
  label: string;
  description: string;
}

const NOTIFICATION_TYPES: NotificationTypeMeta[] = [
  {
    type: Routemize_Constants_NotificationType.REQUEST,
    label: 'New request submitted',
    description: 'A customer submits a new service request',
  },
  {
    type: Routemize_Constants_NotificationType.CONTACT_CAPTURED,
    label: 'Request started',
    description: "A customer starts a request but doesn't submit or book yet",
  },
  {
    // Gated: shown on voice-agent plans whose tenant has GPT-Live (useVoiceMessageNotificationsVisible).
    type: Routemize_Constants_NotificationType.VOICE_MESSAGE,
    label: 'Phone message',
    description: 'A caller leaves a message with the AI voice agent',
  },
  {
    type: Routemize_Constants_NotificationType.APPOINTMENT_CREATED,
    label: 'New appointment booked',
    description: 'A new appointment is scheduled',
  },
  {
    type: Routemize_Constants_NotificationType.APPOINTMENT_UPDATED,
    label: 'Appointment rescheduled',
    description: 'An appointment time is changed',
  },
  {
    type: Routemize_Constants_NotificationType.APPOINTMENT_CANCELLED,
    label: 'Appointment cancelled',
    description: 'An appointment is cancelled',
  },
  {
    type: Routemize_Constants_NotificationType.DAILY_INCOMPLETE_REQUESTS,
    label: 'Daily incomplete requests summary',
    description: 'Daily count of incomplete requests from the last 24 hours',
  },
];

type ChannelKey = 'emailEnabled' | 'smsEnabled' | 'pushEnabled' | 'inAppEnabled';

interface ChannelMeta {
  key: ChannelKey;
  label: string;
  Icon: React.FC<{ className?: string }>;
}

const CHANNELS: ChannelMeta[] = [
  { key: 'inAppEnabled', label: 'In-app', Icon: Bell },
  { key: 'emailEnabled', label: 'Email', Icon: Mail },
  { key: 'smsEnabled', label: 'SMS', Icon: MessageSquare },
  { key: 'pushEnabled', label: 'Push', Icon: Smartphone },
];

const UserNotificationPreferencesModal: React.FC<UserNotificationPreferencesModalProps> = ({ isOpen, onClose }) => {
  const [preferences, setPreferences] = useState<Routemize_DTOs_UserNotificationPreferenceDTO[]>([]);
  const [pendingByType, setPendingByType] = useState<Set<Routemize_Constants_NotificationType>>(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const showVoiceMessage = useVoiceMessageNotificationsVisible(isOpen);
  const visibleTypes = NOTIFICATION_TYPES.filter(
    meta => showVoiceMessage || meta.type !== Routemize_Constants_NotificationType.VOICE_MESSAGE,
  );

  useEffect(() => {
    if (!isOpen) return;
    void loadPreferences();
  }, [isOpen]);

  const loadPreferences = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await UserNotificationPreferencesService.getApiAppUserNotificationPreferencesGetMine();
      setPreferences(data);
    } catch (err) {
      console.error('Failed to load notification preferences:', err);
      setError('Failed to load notification preferences');
    } finally {
      setIsLoading(false);
    }
  };

  const findPref = (type: Routemize_Constants_NotificationType): Routemize_DTOs_UserNotificationPreferenceDTO | undefined =>
    preferences.find(p => p.notificationType === type);

  const handleToggle = async (type: Routemize_Constants_NotificationType, channel: ChannelKey) => {
    const current = findPref(type);
    if (!current) return;

    const next: Routemize_DTOs_UpdateUserNotificationPreferenceDTO = {
      notificationType: type,
      emailEnabled: current.emailEnabled ?? false,
      smsEnabled: current.smsEnabled ?? false,
      pushEnabled: current.pushEnabled ?? false,
      inAppEnabled: current.inAppEnabled ?? false,
    };
    next[channel] = !(current[channel] ?? false);

    setPendingByType(prev => new Set(prev).add(type));

    // Optimistic local update so the toggle feels instant.
    setPreferences(prev =>
      prev.map(p =>
        p.notificationType === type
          ? { ...p, [channel]: next[channel], isCustomized: true }
          : p,
      ),
    );

    try {
      const saved = await UserNotificationPreferencesService.postApiAppUserNotificationPreferencesUpsertMine(next);
      setPreferences(prev =>
        prev.map(p => (p.notificationType === type ? saved : p)),
      );
    } catch (err) {
      console.error('Failed to update notification preference:', err);
      // Revert on failure
      setPreferences(prev =>
        prev.map(p =>
          p.notificationType === type
            ? { ...p, [channel]: current[channel] ?? false, isCustomized: current.isCustomized ?? false }
            : p,
        ),
      );
      setError('Failed to update preference. Please try again.');
    } finally {
      setPendingByType(prev => {
        const copy = new Set(prev);
        copy.delete(type);
        return copy;
      });
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="fixed inset-0 z-50 overflow-hidden">
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose} />

      <div className="absolute inset-y-0 right-0 max-w-[680px] w-full bg-surface shadow-xl transform transition-transform duration-300 ease-out">
        <div className="h-full flex flex-col min-h-0">
          {/* Header */}
          <div className="px-6 py-4 border-b border-ink-200 flex items-center flex-shrink-0">
            <button
              onClick={onClose}
              className="p-2 hover:bg-ink-100 rounded-full transition-colors mr-3"
            >
              <ArrowLeft className="h-5 w-5 text-ink-600" />
            </button>
            <div>
              <h2 className="text-lg font-semibold text-ink-900">Notification Preferences</h2>
              <p className="text-xs text-ink-500 mt-0.5">Choose which channels you'd like for each event.</p>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6 min-h-0">
            {isLoading ? (
              <div className="flex justify-center py-16">
                <LoadingSpinner />
              </div>
            ) : (
              <div className="space-y-4">
                {error && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                )}

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800">
                    Until you change a row, defaults from your company's notification settings apply.
                    Toggling any channel saves your personal preference for that notification.
                  </p>
                </div>

                {/* Matrix header */}
                <div className="hidden sm:grid grid-cols-[1fr_repeat(4,72px)] gap-2 px-3 pb-2 text-11 font-semibold text-ink-500 uppercase tracking-wide">
                  <div>Notification</div>
                  {CHANNELS.map(c => (
                    <div key={c.key} className="text-center">{c.label}</div>
                  ))}
                </div>

                {visibleTypes.map(meta => {
                  const pref = findPref(meta.type);
                  if (!pref) return null;
                  const isPending = pendingByType.has(meta.type);

                  return (
                    <div
                      key={meta.type}
                      className="bg-ink-50 rounded-lg border border-ink-200 p-3 sm:grid sm:grid-cols-[1fr_repeat(4,72px)] sm:gap-2 sm:items-center"
                    >
                      <div className="mb-3 sm:mb-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-ink-900 text-sm">{meta.label}</span>
                          {!pref.isCustomized && (
                            <span className="px-1.5 py-0.5 text-2xs font-medium rounded-full bg-ink-200 text-ink-600">
                              default
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-ink-500 mt-0.5">{meta.description}</p>
                      </div>

                      <div className="grid grid-cols-4 gap-2 sm:contents">
                        {CHANNELS.map(c => {
                          const enabled = pref[c.key] ?? false;
                          return (
                            <div key={c.key} className="flex flex-col items-center gap-1 sm:gap-0">
                              <span className="text-2xs text-ink-500 sm:hidden">{c.label}</span>
                              <Switch
                                checked={enabled}
                                onCheckedChange={() => handleToggle(meta.type, c.key)}
                                disabled={isPending}
                                aria-label={`Toggle ${c.label} for ${meta.label}`}
                              />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-ink-200 flex-shrink-0">
            <button
              onClick={onClose}
              className="w-full bg-ink-900 text-white py-3 px-4 rounded-lg font-medium hover:bg-ink-800 transition-colors"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default UserNotificationPreferencesModal;
